<script setup>
// AI 引流落地页工具（nuxt#7）。复刻 design 的 ai-bg-preview.js / ai-enhance-preview.js：
// 上传 → 浏览器内即时预览（去背景 flood-fill / 放大 lo→hi 演示）+ 前后对比滑块 → CTA 跳主 AI 产品页。
// 本身不做真 AI，是 SEO 引流页。mode: "bg"(去背景) | "enhance"(增强/放大)。
import { ref, computed, nextTick } from "vue";
import { fileTooLargeMsg } from "@/settings/free-tools/batch-limits.js";

const props = defineProps({
  mode: { type: String, required: true },
  slug: { type: String, default: "" },
});

const { downloadBlob } = useFreeToolsDownload();

const ACC = {
  avif: ["AVIF", "image/avif"], gif: ["GIF", "image/gif"], svg: ["SVG", "image/svg+xml,.svg"], ico: ["ICO", "image/x-icon,.ico"],
  webp: ["WEBP", "image/webp"], tiff: ["TIFF", "image/tiff,.tif,.tiff"], bmp: ["BMP", "image/bmp"], psd: ["PSD", ".psd"], dds: ["DDS", ".dds"],
  png: ["PNG", "image/png"], jpg: ["JPG", "image/jpeg"], jpeg: ["JPEG", "image/jpeg"], jfif: ["JFIF", "image/jpeg,.jfif"], heic: ["HEIC", ".heic,.heif"],
  heif: ["HEIF", ".heif"], cr2: ["CR2", ".cr2"], cr3: ["CR3", ".cr3"], nef: ["NEF", ".nef"], dng: ["DNG", ".dng"], arw: ["ARW", ".arw"],
  eps: ["EPS", ".eps"], cdr: ["CDR", ".cdr"], dwg: ["DWG", ".dwg"], dxf: ["DXF", ".dxf"], tga: ["TGA", ".tga"], exr: ["EXR", ".exr"],
  cmyk: ["CMYK", ".cmyk,.jpg"], apng: ["APNG", "image/apng,.apng"], hdr: ["HDR", ".hdr"], xcf: ["XCF", ".xcf"], emf: ["EMF", ".emf"], map: ["MAP", ".map"], jbg: ["JBG", ".jbg"], ps3: ["PS3", ".ps3"],
};
const fmt = computed(() => {
  const m = String(props.slug || "").match(/^([a-z0-9]+)-/);
  return (m && ACC[m[1]]) || ["Image", "image/*"];
});
const fmtLabel = computed(() => fmt.value[0]);
const accept = computed(() => fmt.value[1]);
const isBg = computed(() => props.mode === "bg");
const ctaHref = computed(() => (isBg.value ? "/ai/image/background-remover" : "/ai/image/image-upscaler"));
const ctaText = computed(() => (isBg.value ? "Get a perfect AI cut-out →" : "Upscale & enhance with AI →"));
const dzSub = computed(() => (isBg.value ? "Powered by SharkFoto AI background removal" : "Powered by SharkFoto AI upscaling"));

const stage = ref("drop"); // drop | work
const fileName = ref("");
const split = ref(50);
const previewable = ref(true);
const fileInput = ref(null);
const baseCanvas = ref(null); // bg: cutout / enhance: hi
const overlayCanvas = ref(null); // enhance: lo
const beforeUrl = ref(""); // bg: original img url
let cutCanvas = null;
let lastUrl = "";

const overlayClip = computed(() => `inset(0 ${100 - split.value}% 0 0)`);

/* ---- 去背景：从四边 flood-fill 背景 → 透明 ---- */
function cutout(img) {
  const max = 1100;
  let w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
  const s = Math.min(1, max / Math.max(w, h));
  w = Math.max(1, Math.round(w * s)); h = Math.max(1, Math.round(h * s));
  const c = document.createElement("canvas"); c.width = w; c.height = h;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(img, 0, 0, w, h);
  let id;
  try { id = ctx.getImageData(0, 0, w, h); } catch (e) { return c; }
  const d = id.data, N = w * h;
  const cs = [0, w - 1, (h - 1) * w, (h - 1) * w + (w - 1)];
  let br = 0, bg = 0, bb = 0;
  cs.forEach((p) => { const o = p * 4; br += d[o]; bg += d[o + 1]; bb += d[o + 2]; });
  br /= 4; bg /= 4; bb /= 4;
  const tol2 = 46 * 46;
  const visited = new Uint8Array(N);
  const stk = new Int32Array(N * 2); let sp = 0;
  for (let x = 0; x < w; x++) { stk[sp++] = x; stk[sp++] = (h - 1) * w + x; }
  for (let y = 0; y < h; y++) { stk[sp++] = y * w; stk[sp++] = y * w + (w - 1); }
  while (sp > 0) {
    const p = stk[--sp];
    if (visited[p]) continue; visited[p] = 1;
    const o = p * 4, dr = d[o] - br, dg = d[o + 1] - bg, db = d[o + 2] - bb;
    if (dr * dr + dg * dg + db * db > tol2) continue;
    d[o + 3] = 0;
    const x = p % w, y = (p / w) | 0;
    if (x > 0 && sp < stk.length) stk[sp++] = p - 1;
    if (x < w - 1 && sp < stk.length) stk[sp++] = p + 1;
    if (y > 0 && sp < stk.length) stk[sp++] = p - w;
    if (y < h - 1 && sp < stk.length) stk[sp++] = p + w;
  }
  const a0 = new Uint8Array(N); for (let i = 0; i < N; i++) a0[i] = d[i * 4 + 3];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = y * w + x; if (a0[i] === 0) continue;
    if ((x > 0 && a0[i - 1] === 0) || (x < w - 1 && a0[i + 1] === 0) || (y > 0 && a0[i - w] === 0) || (y < h - 1 && a0[i + w] === 0)) d[i * 4 + 3] = 140;
  }
  ctx.putImageData(id, 0, 0);
  return c;
}

/* ---- 增强/放大演示：lo(像素化) + hi(平滑)，仅示意「AI 重建的样子」 ---- */
function buildDemo(img) {
  const W = 560;
  let w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
  const dw = W, dh = Math.max(1, Math.round(W * (h / Math.max(1, w))));
  const hi = document.createElement("canvas"); hi.width = dw; hi.height = dh;
  const hx = hi.getContext("2d"); hx.imageSmoothingQuality = "high"; hx.drawImage(img, 0, 0, dw, dh);
  const lo = document.createElement("canvas"); lo.width = dw; lo.height = dh;
  const lx = lo.getContext("2d");
  const tiny = document.createElement("canvas"); const tf = 0.16;
  tiny.width = Math.max(2, Math.round(dw * tf)); tiny.height = Math.max(2, Math.round(dh * tf));
  tiny.getContext("2d").drawImage(img, 0, 0, tiny.width, tiny.height);
  lx.imageSmoothingEnabled = false; lx.drawImage(tiny, 0, 0, dw, dh);
  return { lo, hi, dw, dh };
}

function revoke() { if (lastUrl) { URL.revokeObjectURL(lastUrl); lastUrl = ""; } }

async function onFile(file) {
  if (!file) return;
  const ok = file.type.startsWith("image/") || /\.(psd|dds|tiff?|svg|heic|heif|avif|webp|bmp|ico|gif|cr2|cr3|nef|dng|arw|eps|tga|exr)$/i.test(file.name);
  if (!ok) { alert("Please choose an image file."); return; }
  const _m = fileTooLargeMsg(file, "image");
  if (_m) { alert(_m); return; }
  fileName.value = file.name;
  revoke();
  const url = URL.createObjectURL(file); lastUrl = url;
  const img = new Image();
  img.onload = async () => {
    previewable.value = true;
    split.value = 50;
    stage.value = "work";
    await nextTick();
    try {
      if (isBg.value) {
        const cut = cutout(img); cutCanvas = cut;
        const cv = baseCanvas.value; if (cv) { cv.width = cut.width; cv.height = cut.height; cv.getContext("2d").drawImage(cut, 0, 0); }
        beforeUrl.value = url;
      } else {
        const d = buildDemo(img);
        const hi = baseCanvas.value, lo = overlayCanvas.value;
        if (hi) { hi.width = d.dw; hi.height = d.dh; hi.getContext("2d").drawImage(d.hi, 0, 0); }
        if (lo) { lo.width = d.dw; lo.height = d.dh; lo.getContext("2d").drawImage(d.lo, 0, 0); }
      }
    } catch (e) { previewable.value = false; }
  };
  img.onerror = () => { previewable.value = false; stage.value = "work"; }; // raw/psd 不可预览：仍进 CTA
  img.src = url;
}

function pick() { fileInput.value && fileInput.value.click(); }
function onInput(e) { const f = e.target.files && e.target.files[0]; if (f) onFile(f); }
function onDrop(e) { e.preventDefault(); dragging.value = false; const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) onFile(f); }
function reset() { stage.value = "drop"; previewable.value = true; cutCanvas = null; if (fileInput.value) fileInput.value.value = ""; }
function downloadPreview() {
  if (!cutCanvas) return;
  cutCanvas.toBlob((b) => downloadBlob(b, "cutout-preview.png"), "image/png");
}

const dragging = ref(false);
const stageEl = ref(null);
let sliding = false;
function setSplit(clientX) {
  const el = stageEl.value; if (!el) return;
  const r = el.getBoundingClientRect();
  split.value = Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100));
}
function onHandleDown(e) { sliding = true; try { e.target.setPointerCapture(e.pointerId); } catch (_) {} }
function onStageDown(e) { if (!e.target.classList.contains("cmp-handle")) setSplit(e.clientX); }
function onMove(e) { if (sliding) setSplit(e.clientX); }
function onUp() { sliding = false; }
</script>

<template>
  <div class="ail" @pointermove="onMove" @pointerup="onUp">
    <input ref="fileInput" type="file" :accept="accept" hidden @change="onInput" />

    <!-- 拖放区 -->
    <div v-show="stage === 'drop'" class="dz" :class="{ drag: dragging }"
      @click="pick" @dragenter.prevent="dragging = true" @dragover.prevent="dragging = true"
      @dragleave.prevent="dragging = false" @drop="onDrop">
      <div class="dz-ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3h7v7H3zM14 14h7v7h-7M3 14l4-4 3 3 5-5 4 4" /></svg></div>
      <h3>Drop your {{ fmtLabel }} file here</h3>
      <div class="hint">or click to browse — {{ fmtLabel }} files</div>
      <div class="formats"><span>{{ fmtLabel }}</span></div>
      <div class="dz-or">{{ dzSub }}</div>
      <button class="btn btn-ai" type="button" @click.stop="pick">Choose {{ fmtLabel }}</button>
    </div>

    <!-- 工作区 -->
    <div v-if="stage === 'work'" class="ws">
      <div class="ws-grid">
        <div class="ws-preview">
          <div v-if="previewable" ref="stageEl" class="cmp-stage" :class="{ checker: isBg }" @pointerdown="onStageDown">
            <canvas ref="baseCanvas" class="cmp-base"></canvas>
            <!-- bg: overlay = 原图(左)；enhance: overlay = lo 像素化(左) -->
            <div class="cmp-overlay" :style="{ clipPath: overlayClip }">
              <img v-if="isBg" :src="beforeUrl" class="cmp-layer" alt="original" />
              <canvas v-else ref="overlayCanvas" class="cmp-layer"></canvas>
            </div>
            <div class="cmp-handle" :style="{ left: split + '%' }" @pointerdown="onHandleDown"></div>
          </div>
          <div v-else class="cmp-noprev">Preview not available for this format — continue to AI below.</div>
        </div>

        <div class="ws-panel">
          <div class="ws-file">
            <div class="fn">{{ fileName }}</div>
            <div class="fsz">{{ previewable ? "Drag the slider to compare" : fmtLabel + " file ready" }}</div>
            <button class="rm" type="button" title="Remove" @click="reset"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M18 6L6 18" stroke-linecap="round" /></svg></button>
          </div>
          <p class="ai-note">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 3l1.8 4.4L18 9l-4.2 1.6L12 15l-1.8-4.4L6 9z" /></svg>
            <span v-if="isBg">This instant preview runs in your browser and is best on clear, solid backgrounds. For flawless <strong>hair, fur and busy scenes</strong>, get a studio-grade cut-out with SharkFoto AI.</span>
            <span v-else>This is an illustrative in-browser demo. SharkFoto AI reconstructs real detail to upscale and sharpen your <strong>{{ fmtLabel }}</strong> up to 4K.</span>
          </p>
          <div class="ws-actions">
            <NuxtLink class="btn btn-ai btn-lg" :to="ctaHref">{{ ctaText }}</NuxtLink>
            <button v-if="isBg && previewable" class="btn btn-outline" type="button" @click="downloadPreview">Download free preview (PNG)</button>
            <button class="btn btn-ghost" type="button" @click="reset">Use another file</button>
          </div>
        </div>
      </div>

      <div class="trust">
        <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" /></svg>{{ isBg ? "AI-grade cut-outs" : "AI-grade detail" }}</span>
        <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>One-click, no manual work</span>
        <span class="i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v10M7 12h10" /></svg>Free to try</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ail { width: 100%; }
.dz { border: 2px dashed var(--line-strong); border-radius: 20px; background: var(--surface); padding: 48px 24px; text-align: center; cursor: pointer; transition: 0.18s; }
.dz.drag, .dz:hover { border-color: #8b5cf6; background: linear-gradient(180deg, color-mix(in oklab, #8b5cf6 8%, var(--surface)), var(--bg-2)); }
.dz-ico { width: 56px; height: 56px; margin: 0 auto 14px; color: #8b5cf6; }
.dz-ico svg { width: 100%; height: 100%; }
.dz h3 { font-size: 20px; font-weight: 700; }
.dz .hint { color: var(--tx-dim); font-size: 14px; margin-top: 6px; }
.dz .formats { margin: 12px 0; }
.dz .formats span { font-size: 12px; font-weight: 700; color: var(--tx-faint); border: 1px solid var(--line); border-radius: 7px; padding: 3px 10px; }
.dz-or { color: var(--tx-faint); font-size: 13px; margin: 10px 0 16px; }
.btn-ai { background: linear-gradient(110deg, #8b5cf6, #6d5cff); color: #fff; border: none; }
.btn-ai:hover { filter: brightness(1.08); }
.btn-ghost { background: none; border: none; color: var(--tx-dim); cursor: pointer; }
.ws-grid { display: grid; grid-template-columns: 1fr 360px; gap: 22px; align-items: start; }
.ws-preview { min-width: 0; }
.cmp-stage { position: relative; width: 100%; border-radius: 16px; overflow: hidden; border: 1px solid var(--line); user-select: none; touch-action: none; }
.cmp-stage.checker { background-image: conic-gradient(#2a2d3a 25%, transparent 0 50%, #2a2d3a 0 75%, transparent 0); background-size: 22px 22px; background-color: #1c1e27; }
.cmp-base { display: block; width: 100%; height: auto; }
.cmp-overlay { position: absolute; inset: 0; overflow: hidden; }
.cmp-layer { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; display: block; }
.cmp-handle { position: absolute; top: 0; bottom: 0; width: 3px; background: #8b5cf6; transform: translateX(-50%); cursor: ew-resize; box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.3); }
.cmp-handle::after { content: ""; position: absolute; top: 50%; left: 50%; width: 30px; height: 30px; transform: translate(-50%, -50%); border-radius: 50%; background: #8b5cf6; box-shadow: 0 2px 10px rgba(0, 0, 0, 0.4); }
.cmp-noprev { padding: 50px 20px; text-align: center; color: var(--tx-dim); border: 1px solid var(--line); border-radius: 16px; background: var(--surface); }
.ws-panel { display: flex; flex-direction: column; gap: 16px; }
.ws-file { display: flex; align-items: center; gap: 10px; border: 1px solid var(--line); border-radius: 12px; padding: 12px 14px; background: var(--surface); }
.ws-file .fn { font-weight: 600; font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
.ws-file .fsz { font-size: 12px; color: var(--tx-faint); }
.ws-file .rm { background: none; border: none; color: var(--tx-faint); cursor: pointer; flex: none; }
.ai-note { display: flex; gap: 10px; font-size: 13px; color: var(--tx-dim); line-height: 1.5; background: color-mix(in oklab, #8b5cf6 8%, var(--surface)); border: 1px solid color-mix(in oklab, #8b5cf6 25%, var(--line)); border-radius: 12px; padding: 12px 14px; }
.ai-note svg { width: 18px; height: 18px; color: #8b5cf6; flex: none; margin-top: 1px; }
.ws-actions { display: flex; flex-direction: column; gap: 10px; }
.trust { display: flex; gap: 22px; justify-content: center; flex-wrap: wrap; margin-top: 20px; }
.trust .i { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600; color: var(--tx-dim); }
.trust .i svg { width: 16px; height: 16px; color: #8b5cf6; }
@media (max-width: 760px) { .ws-grid { grid-template-columns: 1fr; } }
</style>
